import type { MeResponse } from "../api-models";
import { hasAnyGrant, isModuleEnabled } from "./permissions";

export interface NavItem {
  key: string;
  label: string;
  path: string;
  description?: string;
  moduleId?: string;
  permission?: string;
  /** Extra path prefixes that belong to this item (detail routes that don't sit under `path`). */
  match?: string[];
}

/**
 * A module is one card on the landing page. Modules with `items` open a hub page
 * (`/m/:key`) listing those items as cards; modules with a `path` link straight to a page.
 */
export interface NavModule {
  key: string;
  label: string;
  description: string;
  /** Icon name from @ant-design/icons, resolved in NavCards. */
  icon: string;
  /** Set for single-page modules (Dashboard, Approvals, Projects, Reports); otherwise the hub is used. */
  path?: string;
  /** Extra path prefixes that belong to this module (detail routes that don't sit under `path`). */
  match?: string[];
  permission?: string;
  moduleId?: string;
  items: NavItem[];
}

export const NAV_MODULES: NavModule[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    description: "Key counts, exceptions, over-consumption and recent stock movement.",
    icon: "dashboard",
    path: "/dashboard",
    items: [],
  },
  {
    key: "approvals",
    label: "Approvals",
    description: "Documents waiting on you, plus anything delegated to you.",
    icon: "approvals",
    path: "/approvals",
    permission: "Approvals.Read",
    items: [],
  },
  {
    key: "operations",
    label: "Operations",
    description: "Day-to-day material movement: requests, issues, receipts and returns.",
    icon: "operations",
    items: [
      {
        key: "requests",
        label: "Material Requests",
        path: "/requests",
        description: "Raise and track MRQs against a project BOM.",
        moduleId: "requests",
        permission: "Requests.Read",
      },
      {
        key: "issues",
        label: "Goods Issues (GIN)",
        path: "/issues",
        description: "Issue allocated material out of a stock location.",
        moduleId: "gin",
        permission: "GIN.Read",
      },
      {
        key: "boat-notes",
        label: "Boat Notes",
        path: "/boat-notes",
        description: "Manifest the issue notes travelling on one boat to a site.",
        moduleId: "boat-notes",
        permission: "BoatNotes.Read",
      },
      {
        key: "receipts",
        label: "Receipts (MRN)",
        path: "/receipts",
        description: "Record material received at a site or central warehouse.",
        moduleId: "mrn",
        permission: "MRN.Read",
      },
      {
        key: "discrepancies",
        label: "Discrepancies",
        path: "/discrepancies",
        description: "Short, damaged or excess quantities raised on receipt.",
        moduleId: "discrepancies",
        permission: "Discrepancies.Read",
      },
      {
        key: "consumption",
        label: "Consumption",
        path: "/consumption",
        description: "Book issued material against BOM activities.",
        moduleId: "consumption",
        permission: "Consumption.Read",
      },
      {
        key: "transfers",
        label: "Transfers",
        path: "/transfers",
        description: "Move stock between locations and track it in transit.",
        moduleId: "transfers",
        permission: "Transfers.Read",
      },
      {
        key: "returns",
        label: "Site Returns",
        path: "/returns",
        description: "Return unused material from a site back to stores.",
        moduleId: "returns",
        permission: "Returns.Read",
      },
      {
        key: "supplier-returns",
        label: "Supplier Returns",
        path: "/supplier-returns",
        description: "Send rejected or surplus material back to a supplier.",
        moduleId: "supplier-returns",
        permission: "SupplierReturns.Read",
      },
      {
        key: "tools",
        label: "Tool Custody",
        path: "/tools",
        description: "Issue and recover tools held by staff.",
        moduleId: "tools",
        permission: "Tools.Read",
      },
    ],
  },
  {
    // Everything about a project (sites, BOM versions, variations, requests) is reached
    // from the project itself, so this module links straight to the register.
    key: "projects",
    label: "Projects",
    description: "Projects, their sites, BOM versions and variations.",
    icon: "projects",
    path: "/projects",
    match: ["/bom-versions", "/bom-variations"],
    moduleId: "projects",
    permission: "Projects.Read",
    items: [],
  },
  {
    key: "stock",
    label: "Stock",
    description: "Balances, movement history and adjustments.",
    icon: "stock",
    items: [
      {
        key: "stock",
        label: "Stock Register",
        path: "/stock",
        description: "Current quantity on hand by item and location.",
        moduleId: "stock",
        permission: "Stock.Read",
      },
      {
        key: "stock-ledger",
        label: "Stock Ledger",
        path: "/stock/ledger",
        description: "Every movement that produced the current balances.",
        moduleId: "stock",
        permission: "Stock.Read",
      },
      {
        key: "adjustments",
        label: "Stock Adjustments",
        path: "/adjustments",
        description: "Correct balances after a physical count or write-off.",
        moduleId: "adjustments",
        permission: "Adjustments.Read",
      },
    ],
  },
  {
    key: "procurement",
    label: "Procurement",
    description: "Requisitions through quotations, orders and goods receipts.",
    icon: "procurement",
    items: [
      {
        key: "prs",
        label: "Purchase Requisitions",
        path: "/procurement/prs",
        description: "Requisitions raised for material that isn't in stock.",
        moduleId: "procurement",
        permission: "PR.Read",
      },
      {
        key: "rfqs",
        label: "Quotation Requests (RFQ)",
        path: "/procurement/rfqs",
        description: "Collect and compare supplier quotations.",
        moduleId: "rfq",
        permission: "Quotations.Read",
      },
      {
        key: "pos",
        label: "Purchase Orders",
        path: "/procurement/pos",
        description: "Orders placed with suppliers, including amendments.",
        moduleId: "procurement",
        permission: "PO.Read",
      },
      {
        key: "grns",
        label: "Goods Receipts",
        path: "/procurement/grns",
        description: "Receipt supplier deliveries against a purchase order.",
        moduleId: "grn",
        permission: "GRN.Read",
      },
      {
        key: "gin-directs",
        label: "GIN Direct",
        path: "/procurement/gin-directs",
        description: "Receive a supplier delivery straight into the site store that requested it.",
        moduleId: "gin-direct",
        permission: "GINDirect.Read",
      },
    ],
  },
  {
    key: "masters",
    label: "Masters",
    description: "Reference data: items, categories, units, tax policies, suppliers and boats.",
    icon: "masters",
    items: [
      {
        key: "items",
        label: "Material master",
        path: "/masters/items",
        description: "Materials with base UOM, BOM eligibility, stock and procurement defaults.",
        moduleId: "items",
        permission: "Items.Read",
      },
      {
        key: "categories",
        label: "Categories",
        path: "/masters/categories",
        description: "Item categories used for grouping and reporting.",
        moduleId: "items",
        permission: "Items.Read",
      },
      {
        key: "units",
        label: "Units",
        path: "/masters/units",
        description: "Units of measure available to items.",
        moduleId: "items",
        permission: "Items.Read",
      },
      {
        key: "tax-policies",
        label: "Tax Policies",
        path: "/masters/tax-policies",
        description: "Named tax rates a material carries and a purchase order line applies.",
        moduleId: "items",
        permission: "Items.Read",
      },
      {
        key: "suppliers",
        label: "Suppliers",
        path: "/masters/suppliers",
        description: "Supplier register used by RFQs and purchase orders.",
        moduleId: "suppliers",
        permission: "Suppliers.Read",
      },
      {
        key: "boats",
        label: "Boats",
        path: "/masters/boats",
        description: "Vessel register used by boat notes.",
        moduleId: "boats",
        permission: "Boats.Read",
      },
    ],
  },
  {
    key: "attendance",
    label: "Attendance",
    description: "Staff master, work calendar and daily attendance sheets.",
    icon: "attendance",
    items: [
      {
        key: "staff",
        label: "Staff",
        path: "/staff",
        description: "Staff register with trades, rates and site assignment.",
        moduleId: "staff",
        permission: "Staff.Read",
      },
      {
        key: "work-calendar",
        label: "Work Calendar",
        path: "/work-calendar",
        description: "Working days, holidays and shift patterns.",
        moduleId: "work-calendar",
        permission: "WorkCalendar.Read",
      },
      {
        key: "attendance",
        label: "Daily Attendance",
        path: "/attendance",
        description: "Daily sheets, approvals and overtime.",
        moduleId: "attendance",
        permission: "Attendance.Read",
      },
      {
        key: "attendance-rules",
        label: "Attendance Rules",
        path: "/attendance-rules",
        description: "Standard working window, break, rounding and the overtime split.",
        moduleId: "attendance",
        permission: "Attendance.Read",
      },
    ],
  },
  {
    key: "services",
    label: "Service Requests",
    description: "Maintenance and vehicle service requests.",
    icon: "services",
    items: [
      {
        key: "service-requests",
        label: "Service Requests",
        path: "/service-requests",
        description: "Raise and track service and maintenance jobs.",
        moduleId: "service-requests",
        permission: "ServiceRequests.Read",
      },
      {
        key: "vehicles",
        label: "Vehicles",
        path: "/vehicles",
        description: "Vehicle register used by service requests.",
        moduleId: "vehicles",
        permission: "Vehicles.Read",
      },
    ],
  },
  {
    key: "reports",
    label: "Reports",
    description: "Every project, stock and procurement report.",
    icon: "reports",
    path: "/reports",
    permission: "Reports.Read",
    items: [],
  },
  {
    key: "admin",
    label: "Administration",
    description: "Users, roles, approval workflows and the audit log.",
    icon: "admin",
    items: [
      { key: "admin-users", label: "Users", path: "/admin/users", description: "User accounts and role assignment.", permission: "Users.Read" },
      {
        key: "admin-roles",
        label: "Roles",
        path: "/admin/roles",
        description: "Custom roles and the permissions they grant.",
        permission: "Roles.Read",
      },
      {
        key: "admin-workflows",
        label: "Approval Workflows",
        path: "/admin/workflows",
        description: "Approval steps and thresholds per document type.",
        permission: "Settings.Write",
      },
      {
        key: "admin-stock-locations",
        label: "Stock Locations",
        path: "/admin/stock-locations",
        description: "Central warehouses and site stores.",
        permission: "StockLocations.Read",
      },
      {
        key: "admin-delegations",
        label: "Approval Delegations",
        path: "/admin/delegations",
        description: "Temporary hand-over of approval authority.",
        permission: "Approvals.Read",
      },
      { key: "admin-audit", label: "Audit Log", path: "/admin/audit", description: "Who changed what, and when.", permission: "Audit.Read" },
      {
        key: "admin-developer",
        label: "Developer Options",
        path: "/admin/developer",
        description: "Build and demo tooling, including generating sample data.",
        moduleId: "developer",
        permission: "Developer.Read",
      },
    ],
  },
  {
    // Open to everyone: a guide runs on sample data under a sample user, so no real grant
    // is needed to follow one (tutorials/engine/session.ts).
    key: "tutorials",
    label: "Tutorials",
    description: "Step-by-step walkthroughs of how each part of Glut works, on sample data.",
    icon: "tutorials",
    path: "/tutorials",
    items: [],
  },
];

export function modulePath(mod: NavModule): string {
  return mod.path ?? `/m/${mod.key}`;
}

function canSee(user: MeResponse | null, entry: { moduleId?: string; permission?: string }): boolean {
  if (entry.moduleId && !isModuleEnabled(user, entry.moduleId)) return false;
  if (entry.permission && !hasAnyGrant(user, entry.permission)) return false;
  return true;
}

export function visibleItems(mod: NavModule, user: MeResponse | null): NavItem[] {
  if (!user) return [];
  return mod.items.filter((item) => canSee(user, item));
}

/** Modules the user can reach: single-page modules they hold the permission for, hubs with at least one visible item. */
export function visibleModules(user: MeResponse | null): NavModule[] {
  if (!user) return [];
  return NAV_MODULES.filter((mod) => {
    if (!canSee(user, mod)) return false;
    return mod.items.length === 0 || visibleItems(mod, user).length > 0;
  });
}

export function findModule(key: string | undefined): NavModule | undefined {
  return NAV_MODULES.find((m) => m.key === key);
}

function pathMatches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

/** The module + item owning a pathname, longest prefix first so `/stock/ledger` beats `/stock`. */
export function locateNav(pathname: string): { module?: NavModule; item?: NavItem } {
  let best: { module: NavModule; item?: NavItem; length: number } | undefined;

  for (const mod of NAV_MODULES) {
    const candidates: { item?: NavItem; prefixes: string[] }[] = [
      { prefixes: [mod.path ?? `/m/${mod.key}`, ...(mod.match ?? [])] },
      ...mod.items.map((item) => ({ item, prefixes: [item.path, ...(item.match ?? [])] })),
    ];
    for (const candidate of candidates) {
      for (const prefix of candidate.prefixes) {
        if (pathMatches(pathname, prefix) && (!best || prefix.length > best.length)) {
          best = { module: mod, item: candidate.item, length: prefix.length };
        }
      }
    }
  }

  return best ? { module: best.module, item: best.item } : {};
}
